'use client';

import { Dictionary } from '@/lib/get-dictionary';
import { BrewConfig } from '@/server-actions/fetch-product-config';
import { QueryCustomerSessionResult } from '@/server-actions/query-customer-session';
import { ProductConfig } from '@sales-domain/product-config';
import { List } from '@soluto-private/mx-asurion-ui-react';
import clsx from 'clsx';
import Image from 'next/image';
import { ExtractedBillingFrequencyMonths } from '../BillingPreferenceCards/types';
import { BillingInfoProp } from '../ProductOfferSummary/CustomerPaymentInfo';

interface PaymentListProps {
  billingInfo?: BillingInfoProp;
  dictionary?: Dictionary;
  productConfig: Partial<ProductConfig> | BrewConfig;
  taxValue: number;
  customerSessionQuery: QueryCustomerSessionResult;
  productPaidFrequency?: ExtractedBillingFrequencyMonths;
}
const cardIconPath = `//assets.asurion-ui.my.asurion53.com/images/badges/payment/`;

function isPartialProductConfig(config: any): config is Partial<ProductConfig> {
  return config && 'name' in config;
}

function isBrewConfig(config: any): config is BrewConfig {
  return config && 'retailName' in config;
}

export default function PaymentList(props: PaymentListProps) {
  const {
    billingInfo,
    customerSessionQuery,
    dictionary,
    productConfig,
    taxValue,
    productPaidFrequency,
  } = props;
  const priceValue = parseFloat(productConfig.price ?? '0');

  const productName = isPartialProductConfig(productConfig)
    ? productConfig.name
    : isBrewConfig(productConfig)
      ? productConfig.retailName
      : '';

  const frequency =
    productPaidFrequency === ExtractedBillingFrequencyMonths.TWELVE
      ? 'annual'
      : 'monthly';

  return (
    <div>
      <List
        maxItemsPerRow={1}
        className={clsx(
          'grid w-full flex-shrink-0 grid-rows-[repeat(3_2rem)] items-center gap-0 overflow-hidden rounded-lg border border-solid border-[#e6e6eb] bg-[#fcfcfc] leading-8 last:border-b-0 [&>div]:p-4 [&>div]:leading-tight [&>li]:border-x-0 [&>li]:border-b-[1px] [&>li]:border-t-0 [&>li]:border-solid [&>li]:border-[#e6e6eb]',
          {
            ['grid-cols-[auto_160px]']: !!billingInfo?.brand,
            ['grid-cols-[auto_100px]']: !billingInfo?.brand,
          },
        )}
        data-testid="monthly-payment"
      >
        <List.Item>
          <List.Item.Leading
            className={`overflow-hidden text-ellipsis whitespace-nowrap pr-4 !text-[#5D5E61]`}
          >
            {dictionary?.product.name ?? productName}
          </List.Item.Leading>
          <List.Item.Trailing className={`text-right`}>
            ${productConfig.price}
          </List.Item.Trailing>
        </List.Item>

        <List.Item>
          <List.Item.Leading
            className={`overflow-hidden text-ellipsis whitespace-nowrap pr-4 !text-[#5D5E61]`}
          >
            {dictionary?.priceBreakdown.tax ?? 'Tax'}
          </List.Item.Leading>
          <List.Item.Trailing className="flex justify-end">
            <div className="whitespace-nowrap">
              {taxValue >= 0 &&
              customerSessionQuery.success &&
              customerSessionQuery.data.customerSession?.customer?.address
                ? `$${taxValue.toFixed(2)}`
                : 'calculated at next step'}
            </div>
          </List.Item.Trailing>
        </List.Item>

        <List.Item className={clsx('!bg-[#FAF9FC] text-right')}>
          <List.Item.Leading
            className={`overflow-hidden text-ellipsis whitespace-nowrap pr-4 !text-[#5D5E61]`}
          >
            {dictionary?.priceBreakdown.total ?? `Total ${frequency} cost`}
          </List.Item.Leading>
          <List.Item.Trailing className={`text-right`}>
            {taxValue >= 0 &&
            customerSessionQuery.success &&
            customerSessionQuery.data.customerSession?.customer?.address ? (
              <strong>${(priceValue + taxValue).toFixed(2)}</strong>
            ) : (
              <strong>${productConfig.price}</strong>
            )}
          </List.Item.Trailing>
          {billingInfo?.brand && billingInfo?.lastFour && <List.Item.Divider />}
        </List.Item>

        {billingInfo?.brand && billingInfo?.lastFour && (
          <List.Item>
            <List.Item.Leading className="pr-4">
              Payment method
            </List.Item.Leading>
            <List.Item.Trailing className="text-1 flex items-center whitespace-nowrap text-[#6E767D]">
              <Image
                src={`${cardIconPath}${billingInfo.brand}.svg`}
                width={38}
                height={24}
                alt={`${billingInfo.brand} Icon`}
                className="mr-1"
              />
            </List.Item.Trailing>
          </List.Item>
        )}
      </List>
    </div>
  );
}
